// SPDX-License-Identifier: AGPL-3.0-or-later

import type { SVGProps } from 'react';
import { useMe } from '../api/auth.js';

/**
 * Brand symbol — a softly-rounded clipboard with a flowing checkmark
 * line, sized for a 24 px nav slot. Single-colour, drives off
 * `currentColor` so the host can re-tint by setting `color:` on a
 * wrapping element. Kept inline (no external SVG file) so the symbol
 * renders without a network round-trip and inherits text colour for
 * dark-mode swaps.
 */
export function BrandSymbol(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 32 32"
      role="img"
      aria-label="Intakeform"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* Clipboard body — rounded rectangle with a clipped tab on top. */}
      <path
        d="M9 6.5h14a3 3 0 0 1 3 3v15a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3v-15a3 3 0 0 1 3-3Z"
        fill="currentColor"
        opacity="0.12"
      />
      <path
        d="M9 6.5h14a3 3 0 0 1 3 3v15a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3v-15a3 3 0 0 1 3-3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      {/* Clipboard clip — small rounded square at the top centre. */}
      <rect x="12" y="3.5" width="8" height="5" rx="1.5" fill="currentColor" />
      {/* Flowing checkmark — drawn as a single stroke that suggests
       * a form completing rather than a corporate-tick. */}
      <path
        d="M11 17.5l3 3 7-7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Brand wordmark — renders either the organization's custom brand name
 * if configured, or falls back to "intakeform".
 */
export function BrandWordmark(props: SVGProps<SVGSVGElement>) {
  const { data: me } = useMe();
  const settings = me?.organization?.settings as { brand_name?: unknown } | undefined;
  const brandName = typeof settings?.brand_name === 'string' ? settings.brand_name : 'intakeform';

  return (
    <span
      className={props.className}
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: '18px',
        fontWeight: 600,
        letterSpacing: '-0.02em',
        color: 'currentColor',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '180px',
        display: 'inline-block',
        lineHeight: '1.2',
      }}
    >
      {brandName}
    </span>
  );
}

export interface BrandLinkProps {
  /** Where the brand link points. Defaults to `/` (which resolves to
   * the dashboard via the router's root redirect). */
  to?: string;
  /** Render as an anchor (default) or a div — useful when the host
   * already wraps the brand in a router `<Link>`. */
  as?: 'a' | 'div';
}

/**
 * The composed brand mark used in the masthead. Outputs the symbol +
 * wordmark with the canonical spacing and an accessible name so
 * keyboard users can target it.
 */
export function BrandMark({ to = '/', as = 'a' }: BrandLinkProps) {
  const Tag = as;
  const tagProps = as === 'a' ? { href: to, 'aria-label': 'Intakeform — home' } : {};
  return (
    <Tag className="brand-link" {...tagProps}>
      <BrandSymbol className="brand-symbol" aria-hidden="true" focusable="false" />
      <BrandWordmark className="brand-wordmark" />
    </Tag>
  );
}
